import { RawLocation } from 'vue-router'
import { Commit, Dispatch } from 'vuex'

import LoginCredentials from '@/models/login-credentials'
import RegistrationData from '@/models/registration-data'
import ResetPasswordFormData from '@/models/reset-password-form-data'

import axios from '@/axios-module'
import { firstErrorMessage } from '@/globalMixin'
import notification from '@/notification'
import router from '@/router'
import { GlobalMutationTypes } from '@/store'

import { AuthMutationTypes } from './AuthMutationTypes'

export enum AuthActionTypes {
  Login = '[Auth] Login',
  Logout = '[Auth] Logout',
  Registration = '[Auth] Registration',
  ForgotPassword = '[Auth] ForgotPassword',
  ResetPassword = '[Auth] ResetPassword',
}

export default {
  async [AuthActionTypes.Login](
    { commit }: { commit: Commit },
    {
      credentials,
      redirectTo,
    }: { credentials: LoginCredentials; redirectTo?: string },
  ) {
    commit(AuthMutationTypes.Login)
    try {
      const response = await axios.post('/customer/sessions', {
        customer: credentials,
      })
      commit(AuthMutationTypes.LoginSuccess, response.data)
      if (redirectTo) {
        router.push({ path: redirectTo })
      }
    } catch ({ response }) {
      commit(AuthMutationTypes.LoginFailure, response.data.error)
    }
  },
  async [AuthActionTypes.Registration](
    { commit }: { commit: Commit },
    {
      registrationData,
      redirectTo,
    }: { registrationData: RegistrationData; redirectTo?: string },
  ) {
    commit(AuthMutationTypes.Registration)
    try {
      await axios.post('/customer/registrations', {
        customer: registrationData,
      })
      commit(AuthMutationTypes.RegistrationSuccess)
      if (redirectTo) {
        router.push({ path: redirectTo })
      }
    } catch ({ response }) {
      commit(AuthMutationTypes.RegistrationFailure, response.data.errors)
    }
  },
  async [AuthActionTypes.ForgotPassword](
    { commit }: { commit: Commit },
    email: string,
  ) {
    commit(AuthMutationTypes.ForgotPassword)
    try {
      const response = await axios.post('/customer/password', {
        customer: { email },
      })
      commit(AuthMutationTypes.ForgotPasswordSuccess, response.data)
      notification(
        'A message has been sent to you by email with instructions on how to reset your password.',
        'success',
      )

      router.push({ name: 'login' })
    } catch ({ response }) {
      commit(AuthMutationTypes.ForgotPasswordFailure)
      notification(
        `Error happened, please try again. ${firstErrorMessage(
          response.data.errors,
        )}.`,
        'error',
      )
    }
  },
  async [AuthActionTypes.ResetPassword](
    { commit }: { commit: Commit },
    formData: ResetPasswordFormData,
  ) {
    commit(AuthMutationTypes.ResetPassword)
    try {
      const response = await axios.put('/customer/password', {
        customer: formData,
      })
      commit(AuthMutationTypes.ResetPasswordSuccess, response.data)
      notification('Password reset successful.', 'success')
      router.push({ name: 'dashboard' })
    } catch ({ response }) {
      commit(AuthMutationTypes.ResetPasswordFailure, response.data.errors)
      notification(
        'Error happened, please try again. If this error persists, please contact our support.',
        'error',
      )
    }
  },
  async [AuthActionTypes.Logout](
    { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
    redirectRoute: RawLocation = { name: 'login' },
  ) {
    commit(AuthMutationTypes.Logout)
    try {
      await axios.delete('/customer/sessions')
      commit(AuthMutationTypes.LogoutSuccess)
      dispatch(GlobalMutationTypes.Reset)
      router.push(redirectRoute)
    } catch ({ response }) {
      commit(AuthMutationTypes.LogoutFailure)
      router.push({ name: 'dashboard' })
    }
  },
}
